import type { ComponentChildren } from 'preact';

export interface TickerItem {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'pos' | 'neg' | 'accent' | 'mute';
  readonly icon?: ComponentChildren;
}

interface TickerProps {
  readonly items: readonly TickerItem[];
}

/**
 * Bloomberg-style live ticker tape. Items scroll seamlessly; the sequence is
 * duplicated so the loop has no visible seam. Motion is suppressed for users
 * who prefer reduced motion (handled in CSS).
 */
export function Ticker({ items }: TickerProps) {
  if (items.length === 0) return null;

  const renderRun = (keyPrefix: string) =>
    items.map((item, i) => (
      <span class="ticker-item" key={`${keyPrefix}-${i}`}>
        {item.icon && <span class="ticker-item-icon">{item.icon}</span>}
        <span class="ticker-item-label">{item.label}</span>
        <span class={`ticker-item-value${item.tone ? ` tone-${item.tone}` : ''}`}>
          {item.value}
        </span>
      </span>
    ));

  // Adaptive speed: the rail grows with the item count, so scale the duration
  // with it to keep a calm, readable pace regardless of how many coins load.
  const durationSec = Math.max(40, items.length * 4.5);

  return (
    <div class="ticker" role="marquee" aria-label="Сводка показателей">
      <div class="ticker-rail" style={{ animationDuration: `${durationSec}s` }}>
        <div class="ticker-run">{renderRun('a')}</div>
        <div class="ticker-run" aria-hidden="true">{renderRun('b')}</div>
      </div>
    </div>
  );
}
